// Game clock (PRD section 10): continuous time with pause and ×1, ×2 and ×4. The simulation always
// advances in fixed ticks of game time, so its results never depend on the frame rate; the clock
// only decides how many ticks each frame owes.
import { TICK_MS } from './flow';

export type Speed = 1 | 2 | 4;
export const SPEEDS: readonly Speed[] = [1, 2, 4];

export const isSpeed = (value: unknown): value is Speed => value === 1 || value === 2 || value === 4;

/** A frame longer than this (a hidden tab, a stall) is cut short: the factory stops while away (D10). */
export const MAX_FRAME_MS = 250;

export class Clock {
  speed: Speed = 1;
  paused = false;
  /** Game milliseconds owed but not yet run, always less than one tick. */
  private carry = 0;

  /** Game milliseconds already owed to the next tick: what the view adds to the game time to move smoothly between ticks. */
  get pendingMs(): number {
    return this.carry;
  }

  /** Turns real milliseconds since the last frame into the number of ticks to run now. */
  advance(realMs: number): number {
    if (this.paused || !(realMs > 0)) return 0;
    this.carry += Math.min(realMs, MAX_FRAME_MS) * this.speed;
    const ticks = Math.floor(this.carry / TICK_MS);
    this.carry -= ticks * TICK_MS;
    return ticks;
  }

  setSpeed(speed: Speed): void {
    this.speed = speed;
    this.paused = false;
  }

  /** Pausing keeps the speed, so resuming goes back to it. */
  setPaused(paused: boolean): void {
    this.paused = paused;
    if (paused) this.carry = 0;
  }
}
